import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import {
  white,
  gray100,
  gray20,
  gray40,
  primaryButtonBackgroundColor,
} from '../../constants/colors';
import { headerHeight, spHeaderHeight, formLabelWrapper } from '../../constants/location';
import { AuthContext } from '../../contexts/auth';
import { GroupContext } from '../../contexts/group';
import { createGroup } from '../../api/firebase/firestore/group';
import { getAllUsers } from '../../api/firebase/firestore/user';
import { groupCreateSchema } from '../../validation/group';
import type { GroupCreateType } from '../../validation/group/groupType';
import SideBar from '../uiParts/SideBar';
import CustomTextfield from '../uiParts/CustomTextfield';
import GroupMemberSelector from '../uiParts/GroupMemberSelector';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import CustomLoading from '../uiParts/CustomLoading';
import MetaTitle from '../utils/MetaTitle';
import { userRole, registerStatus } from '../../constants/enum';
import type { DocumentData } from 'firebase/firestore';

const styles = {
  formLabelWrapper,
  button: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    '@media screen and (max-width: 960px)': {
      width: '-webkit-fill-available',
    },
  },
  pageContainer: {
    backgroundColor: gray20,
    minHeight: `calc(100vh - ${headerHeight})`,
    display: 'flex',
    flexDirection: 'row',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column',
      minHeight: `calc(100vh - ${spHeaderHeight})`,
    },
  },
  formContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '39px',
    '@media screen and (max-width: 960px)': {
      width: '480px',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      margin: '0 16px',
      p: '32px 16px',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: `calc(100vh - ${headerHeight})`,
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
    width: '560px',
    margin: '0 auto',
  },
  title: {
    '@media screen and (max-width:960px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
};

type MemberOption = {
  uid: string;
  name: string;
  email: string;
};

const GroupCreate: React.FC = () => {
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [selectedUids, setSelectedUids] = useState<string[]>([]);
  const { user, isLoading } = useContext(AuthContext);
  const { refreshGroups } = useContext(GroupContext);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<GroupCreateType>({
    resolver: yupResolver(groupCreateSchema),
    defaultValues: {
      name: '',
      description: '',
      memberUids: [],
    },
  });

  useEffect(() => {
    if (isLoading) return;
    if (user === null) {
      navigate('/');
      return;
    }
    if (user.role !== userRole.admin) {
      navigate('/ai-ocr');
      return;
    }

    const fetchMembers = async (): Promise<void> => {
      const users = await getAllUsers();
      setMembers(
        users
          .filter(
            (u: DocumentData) =>
              u.registerStatus === registerStatus.finished ||
              u.registerStatus === registerStatus.emailRegistered,
          )
          .map((u: DocumentData) => ({
            uid: u.uid,
            name: u.name ?? '',
            email: u.email,
          })),
      );
    };
    fetchMembers();
  }, [user, isLoading]);

  const onSubmit = async (data: GroupCreateType): Promise<void> => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      await createGroup({ ...data, memberUids: selectedUids }, user.uid);
      await refreshGroups();
      navigate('/groups/manage');
    } catch {
      setErrorText('グループの作成に失敗しました。');
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.pageContainer}>
        <SideBar />
        <Typography component="div" sx={styles.container}>
          <Typography component="div" sx={styles.formContainer}>
            <Typography sx={styles.title}>グループ作成</Typography>
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.name)}
                  formMb="24px"
                  helperText={errors.name?.message}
                  labelText="グループ名"
                  placeholder="例: 営業1課"
                  requiredText="必須"
                  width="400px"
                />
              )}
              control={control}
              name="name"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.description)}
                  formMb="24px"
                  helperText={errors.description?.message}
                  labelText="説明"
                  placeholder="グループの説明（任意）"
                  width="400px"
                />
              )}
              control={control}
              name="description"
            />
            <Box sx={{ mb: '24px', width: '400px' }}>
              <GroupMemberSelector
                members={members}
                selectedUids={selectedUids}
                onChange={setSelectedUids}
              />
            </Box>
            <Button
              onClick={handleSubmit(onSubmit)}
              sx={[primaryButtonBackgroundColor, styles.button]}
              variant="contained"
            >
              グループを作成する
            </Button>
          </Typography>
        </Typography>
        <CustomSnackbar
          autoHideDuration={6000}
          open={open}
          onClose={(): void => setOpen(false)}
          text={errorText}
        />
      </Typography>
    </>
  );
};

export default GroupCreate;
